import { createFileRoute, Link } from "@tanstack/react-router";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import {
  ArrowLeft, Users, TrendingUp, TrendingDown, Activity, Search,
  Filter, ArrowUpDown, Download, MoreHorizontal,
} from "lucide-react";

import { AdminShell } from "@/components/admin/admin-shell";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { cn } from "@/lib/utils";
import { Money, Riyal } from "@/components/ui/riyal";

export const Route = createFileRoute("/admin/plans/$id/subscribers")({
  head: () => ({
    meta: [
      { title: "Plan Subscribers — Spayzo Admin" },
      { name: "description", content: "All merchants subscribed to a specific plan." },
    ],
  }),
  component: Page,
});

type SubStatus = "Active" | "Trial" | "Paused" | "Expired";
type PlanKey = "starter" | "growth" | "premium";
type CycleKey = "monthly" | "annual";

const SUBS: {
  merchant: string; location: string; id: string; plan: PlanKey;
  status: SubStatus; cycle: CycleKey; renewal: string; amount: React.ReactNode; joined: string;
}[] = [
  { merchant: "Amber Lounge", location: "Dubai Marina", id: "MRC-0041", plan: "growth", status: "Active", cycle: "monthly", renewal: "Jul 14, 2025", amount: <Money value="249" />, joined: "Jan 14, 2024" },
  { merchant: "The Zen Garden", location: "Downtown Dubai", id: "MRC-0082", plan: "premium", status: "Active", cycle: "annual", renewal: "Mar 02, 2026", amount: <Money value="5,988" />, joined: "Mar 02, 2024" },
  { merchant: "Coco Beach Club", location: "JBR Beach", id: "MRC-0117", plan: "starter", status: "Trial", cycle: "monthly", renewal: "Jun 28, 2025", amount: <Money value="0" />, joined: "Jun 14, 2025" },
  { merchant: "Spice Route", location: "Business Bay", id: "MRC-0063", plan: "growth", status: "Paused", cycle: "monthly", renewal: "—", amount: <Money value="249" />, joined: "Aug 08, 2023" },
  { merchant: "Luna Rooftop", location: "DIFC", id: "MRC-0095", plan: "premium", status: "Expired", cycle: "annual", renewal: "—", amount: <Money value="5,988" />, joined: "Nov 19, 2022" },
  { merchant: "Surf Social", location: "JBR Walk", id: "MRC-0134", plan: "starter", status: "Active", cycle: "monthly", renewal: "Jul 03, 2025", amount: <Money value="99" />, joined: "Jun 03, 2025" },
  { merchant: "Sky Loft", location: "Downtown Dubai", id: "MRC-0152", plan: "growth", status: "Active", cycle: "annual", renewal: "Sep 21, 2025", amount: <Money value="2,748" />, joined: "Sep 21, 2024" },
  { merchant: "Marina View Hub", location: "Dubai Marina", id: "MRC-0168", plan: "starter", status: "Active", cycle: "monthly", renewal: "Jul 19, 2025", amount: <Money value="99" />, joined: "Apr 19, 2025" },
  { merchant: "Palm Studio", location: "Palm Jumeirah", id: "MRC-0173", plan: "premium", status: "Trial", cycle: "monthly", renewal: "Aug 02, 2025", amount: <Money value="0" />, joined: "Jun 18, 2025" },
];

const STATUS_STYLES: Record<SubStatus, string> = {
  Active: "bg-emerald-500/10 text-emerald-700 border-emerald-500/20",
  Trial: "bg-sky-500/10 text-sky-700 border-sky-500/20",
  Paused: "bg-amber-500/10 text-amber-700 border-amber-500/20",
  Expired: "bg-rose-500/10 text-rose-700 border-rose-500/20",
};

const PLAN_PRICE: Record<PlanKey, number> = { starter: 99, growth: 249, premium: 599 };
const PLAN_CHURN: Record<PlanKey, string> = { starter: "4.1%", growth: "2.8%", premium: "1.9%" };

function Page() {
  const { t } = useTranslation();
  const { id } = Route.useParams();
  const planKey = (["starter", "growth", "premium"].includes(id) ? id : "starter") as PlanKey;
  const planName = t(`adminPlans.planNames.${planKey}`);

  const planSubs = SUBS.filter((s) => s.plan === planKey);
  const [query, setQuery] = useState("");

  const filtered = planSubs.filter((s) => {
    const q = query.toLowerCase();
    return !q || s.merchant.toLowerCase().includes(q) || s.id.toLowerCase().includes(q) || s.location.toLowerCase().includes(q);
  });

  const activeCount = planSubs.filter((s) => s.status === "Active").length;
  const mrrValue = planSubs
    .filter((s) => s.status === "Active" || s.status === "Trial")
    .reduce((sum, s) => {
      const m = String(s.amount).replace(/[^0-9]/g, "");
      return sum + (parseInt(m || "0", 10));
    }, 0);

  const STATUS_LABELS: Record<SubStatus, string> = {
    Active: t("adminPlanSubscribers.active"),
    Trial: t("adminPlanSubscribers.trial"),
    Paused: t("adminPlanSubscribers.paused"),
    Expired: t("adminPlanSubscribers.expired"),
  };
  const CYCLE_LABELS: Record<CycleKey, string> = {
    monthly: t("adminPlanSubscribers.monthly"),
    annual: t("adminPlanSubscribers.annual"),
  };

  const KPIS = [
    { label: t("adminPlanSubscribers.kpis.subscribers"), value: planSubs.length.toLocaleString(), sub: t("adminPlanSubscribers.kpis.subscribersSub"), icon: Users },
    { label: t("adminPlanSubscribers.kpis.mrr"), value: <><Riyal /> {mrrValue.toLocaleString()}</>, sub: t("adminPlanSubscribers.kpis.mrrSub"), icon: TrendingUp },
    { label: t("adminPlanSubscribers.kpis.active"), value: activeCount.toLocaleString(), sub: t("adminPlanSubscribers.kpis.activeSub"), icon: Activity },
    { label: t("adminPlanSubscribers.kpis.churn"), value: PLAN_CHURN[planKey], sub: t("adminPlanSubscribers.kpis.churnSub"), icon: TrendingDown },
  ];

  return (
    <AdminShell
      title={t("adminPlanSubscribers.title", { plan: planName })}
      subtitle={t("adminPlanSubscribers.subtitle", { plan: planName })}
      actions={
        <Button asChild variant="outline" className="h-10 rounded-xl">
          <Link to="/admin/plans">
            <ArrowLeft className="h-4 w-4" /> {t("adminPlanSubscribers.back")}
          </Link>
        </Button>
      }
    >
      {/* Plan banner */}
      <Card className="rounded-2xl border-border/60 p-5 shadow-soft">
        <div className="flex flex-wrap items-center justify-between gap-4">
          <div>
            <p className="font-display text-xl font-bold">{planName}</p>
            <p className="mt-1 text-sm text-muted-foreground">
              <span className="font-display text-base font-bold text-foreground"><Riyal /> {PLAN_PRICE[planKey]}</span> / {t("adminPlans.perMonth")}
            </p>
          </div>
          <Badge variant="outline" className="rounded-full border-emerald-500/30 bg-emerald-500/10 px-3 py-1 text-xs font-semibold text-emerald-700">
            {planSubs.length} {t("adminPlanSubscribers.subscribersLabel")}
          </Badge>
        </div>
      </Card>

      {/* KPIs */}
      <div className="mt-6 grid grid-cols-1 gap-4 sm:grid-cols-2 xl:grid-cols-4">
        {KPIS.map((k) => (
          <Card key={k.label} className="rounded-2xl border-border/60 p-5 shadow-soft">
            <span className="grid h-9 w-9 place-items-center rounded-xl bg-primary/10 text-primary">
              <k.icon className="h-4 w-4" />
            </span>
            <p className="mt-4 text-xs font-medium uppercase tracking-wide text-muted-foreground">{k.label}</p>
            <p className="mt-1 font-display text-3xl font-bold tracking-tight">{k.value}</p>
            <p className="mt-1 text-xs text-muted-foreground">{k.sub}</p>
          </Card>
        ))}
      </div>

      {/* Subscribers table */}
      <Card className="mt-8 overflow-hidden rounded-2xl border-border/60 shadow-soft">
        <div className="flex flex-wrap items-center justify-between gap-3 border-b border-border/60 p-5">
          <p className="font-display text-lg font-bold">{t("adminPlans.allSubscribers")}</p>
          <Button variant="outline" className="h-10 rounded-xl">
            <Download className="h-4 w-4" /> {t("adminPlanSubscribers.exportCsv")}
          </Button>
        </div>

        <div className="flex flex-wrap items-center gap-3 border-b border-border/60 p-4">
          <div className="relative flex-1 min-w-[220px]">
            <Search className="pointer-events-none absolute start-3.5 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
            <Input
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder={t("adminPlanSubscribers.searchPlaceholder")}
              className="h-10 rounded-xl border-border/70 bg-surface ps-10"
            />
          </div>
          <Button variant="outline" className="h-10 rounded-xl">
            <Filter className="h-4 w-4" /> {t("adminPlanSubscribers.filters")}
          </Button>
          <Button variant="outline" className="h-10 rounded-xl">
            <ArrowUpDown className="h-4 w-4" /> {t("adminPlanSubscribers.sort")}
          </Button>
        </div>

        {filtered.length === 0 ? (
          <div className="p-10 text-center text-sm text-muted-foreground">
            {t("adminPlanSubscribers.empty")}
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-surface text-[10px] uppercase tracking-wide text-muted-foreground">
                <tr>
                  <th className="px-5 py-3 text-start font-semibold">{t("adminPlanSubscribers.table.merchant")}</th>
                  <th className="px-3 py-3 text-start font-semibold">{t("adminPlanSubscribers.table.status")}</th>
                  <th className="px-3 py-3 text-start font-semibold">{t("adminPlanSubscribers.table.billingCycle")}</th>
                  <th className="px-3 py-3 text-start font-semibold">{t("adminPlanSubscribers.table.nextRenewal")}</th>
                  <th className="px-3 py-3 text-start font-semibold">{t("adminPlanSubscribers.table.amount")}</th>
                  <th className="px-3 py-3 text-start font-semibold">{t("adminPlanSubscribers.table.joined")}</th>
                  <th className="px-5 py-3 text-end font-semibold">{t("adminPlanSubscribers.table.actions")}</th>
                </tr>
              </thead>
              <tbody>
                {filtered.map((s) => (
                  <tr key={s.id} className="border-t border-border/60 hover:bg-surface/50">
                    <td className="px-5 py-3.5">
                      <div className="flex items-center gap-3">
                        <div className="grid h-9 w-9 shrink-0 place-items-center rounded-xl bg-primary/10 text-xs font-semibold text-primary">
                          {s.merchant.split(" ").map((w) => w[0]).slice(0, 2).join("")}
                        </div>
                        <div className="min-w-0">
                          <p className="truncate font-semibold">{s.merchant}</p>
                          <p className="truncate text-xs text-muted-foreground">{s.location} · #{s.id}</p>
                        </div>
                      </div>
                    </td>
                    <td className="px-3 py-3.5">
                      <Badge variant="outline" className={cn("rounded-full text-[10px] font-semibold", STATUS_STYLES[s.status])}>
                        {STATUS_LABELS[s.status]}
                      </Badge>
                    </td>
                    <td className="px-3 py-3.5">{CYCLE_LABELS[s.cycle]}</td>
                    <td className="px-3 py-3.5 text-muted-foreground">{s.renewal}</td>
                    <td className="px-3 py-3.5 font-semibold">{s.amount}</td>
                    <td className="px-3 py-3.5 text-muted-foreground">{s.joined}</td>
                    <td className="px-5 py-3.5 text-end">
                      <Button asChild variant="ghost" size="icon" className="h-8 w-8 rounded-lg">
                        <Link to="/admin/plans/$id" params={{ id: s.id }}>
                          <MoreHorizontal className="h-4 w-4" />
                        </Link>
                      </Button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        <div className="flex flex-wrap items-center justify-between gap-3 border-t border-border/60 p-4 text-xs text-muted-foreground">
          <span>{t("adminPlanSubscribers.showingOf", { count: filtered.length, total: planSubs.length })}</span>
        </div>
      </Card>
    </AdminShell>
  );
}
